import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserProfile } from '../../hooks/use-query';
import {
  getOrGenerateIdentifierId,
  getOrgUnitId,
  normalizeGtmUserRoles
} from '../../utils/gtmUtils';

const GtmCoreDataLayer = ({ entity, authDetails }) => {
  const location = useLocation();
  const lastPushedEvent = useRef(null);
  const { roles: userRoles, isLoading: isProfileLoading } = useUserProfile(
    authDetails
  );

  useEffect(() => {
    if (
      location.pathname.startsWith('/apis/') ||
      location.pathname.startsWith('/async-apis/') ||
      location.pathname.startsWith('/bundles/') ||
      location.pathname === '/login/callback'
    ) {
      lastPushedEvent.current = null;
      return;
    }

    // Wait for auth to be fully initialized
    if (
      typeof authDetails?.isAuthenticated !== 'boolean' ||
      authDetails?.loginPending
    ) {
      return;
    }

    // If authenticated, wait for the entity data and user profile to finish loading
    if (authDetails?.isAuthenticated) {
      const isEntityLoading =
        entity?.loadingEntity === undefined || entity?.loadingEntity === true;
      // If authDetails says the user has an organization, wait until that organization's name is actually fetched
      const isMissingExpectedOrgDetails =
        authDetails?.entityId && !entity?.entity?.name;

      if (isEntityLoading || isMissingExpectedOrgDetails || isProfileLoading) {
        return;
      }
    }

    // Determine page_section based on the pathname
    let pageSection = 'API Directory';
    let pageSubsection = 'Home';
    let apiId = null;

    if (location.pathname !== '/') {
      pageSubsection = 'Not Found';
    }

    const currentEntity = entity?.entity || {};
    const userId = authDetails?.isAuthenticated
      ? authDetails?.userData?.sub || null
      : null;
    const identifierId = getOrGenerateIdentifierId();

    const fortellisId = authDetails?.isAuthenticated
      ? currentEntity.id || authDetails?.entityId || null
      : null;
    const orgName = authDetails?.isAuthenticated
      ? currentEntity.name || null
      : null;
    const rolesPayload = authDetails?.isAuthenticated
      ? normalizeGtmUserRoles(userRoles)
      : null;
    const orgUnitId = authDetails?.isAuthenticated
      ? getOrgUnitId(currentEntity, entity?.entities, fortellisId)
      : null;
    const storeId = orgUnitId || null;
    const storeName = orgName || null;

    // Create a unique key for the current pageview to prevent duplicates
    const currentEventKey = `${location.pathname}-${userId || ''}-${fortellisId || ''}-${
      rolesPayload ? rolesPayload.join(',') : ''
    }`;

    if (lastPushedEvent.current !== currentEventKey) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'fortellis-pageview',
        page_section: pageSection,
        page_subsection: pageSubsection,
        directory_type: 'ISV',
        api_id: apiId,
        api_name: null,
        api_type: null,
        api_prod_version: null,
        api_test_version: null,
        identifier_id: identifierId,
        fortellis_user_id: userId,
        user_id: null,
        enterprise_id: null,
        enterprise_name: null,
        company_id: null,
        store_id: storeId,
        store_name: storeName,
        stores: null,
        product_features: null,
        user_org: orgName || null,
        user_role: rolesPayload,
        page_url: window.location.href || null,
        page_path: location.pathname || null,
        is_impersonated_session: false,
        fortellis_id: fortellisId || null,
        fortellis_org_name: orgName || null,
        product: 'API',
        application: 'Fortellis Network'
      });

      lastPushedEvent.current = currentEventKey;
    }
  }, [
    location.pathname,
    authDetails?.isAuthenticated,
    authDetails?.loginPending,
    authDetails?.entityId,
    authDetails?.userData?.sub,
    authDetails?.userData?.uid,
    authDetails?.sub,
    authDetails?.userData?.email,
    entity?.loadingEntity,
    entity?.entity?.id,
    entity?.entity?.name,
    entity?.entity?.orgUnitId,
    userRoles,
    isProfileLoading
  ]);

  return null;
};

export default GtmCoreDataLayer;
